import React from 'react';
import { useSubscription, TIER_CONFIG } from '../../context/SubscriptionContext';
import { X, Lock, ArrowRight, Crown, Star, Zap, Eye } from 'lucide-react';
import './Subscription.css';

const UpgradeModal = ({ onNavigateToUpgrade, onTrialStart }) => {
  const { 
    showUpgradeModal, 
    closeUpgradeModal, 
    upgradeModalFeature,
    getRequiredTierForFeature,
    canStartTrial,
    startTrial,
    hasUsedTrial
  } = useSubscription();

  if (!showUpgradeModal) return null;

  const featureNames = {
    wizardA: 'Research Methodology Wizard (Full Access)',
    wizardB: 'Data Analysis Wizard',
    wizardC: 'Framework Builder',
    wizardD: 'Data Collection Process',
    wizardE: 'Ethics Approval Wizard',
    reportA: 'Research Methodology Report',
    reportB: 'Data Analysis Report',
    reportC: 'Framework Report',
    reportD: 'Data Collection Report',
    reportE: 'Ethics Approval Report',
    reportCombined: 'Combined Reports',
    learnBasics: 'Framework - Learn the Basics',
    compareMethodology: 'Compare Methodology'
  };

  const feature = upgradeModalFeature?.feature || 'this feature';
  const featureName = featureNames[feature] || feature;
  const requiredTier = upgradeModalFeature?.requiredTier || getRequiredTierForFeature(feature);
  const requiredConfig = TIER_CONFIG[requiredTier];
  
  const trialAvailable = canStartTrial(feature);
  const trialUsed = hasUsedTrial(feature);

  const handleUpgrade = () => {
    closeUpgradeModal();
    if (onNavigateToUpgrade) {
      onNavigateToUpgrade();
    }
  };
  
  const handleStartTrial = () => {
    if (startTrial(feature)) {
      if (onTrialStart) {
        onTrialStart(feature);
      }
    }
  };

  const getTierIcon = () => {
    switch (requiredTier) {
      case 'super':
        return <Crown size={32} className="modal-tier-icon super" />;
      case 'advanced':
        return <Star size={32} className="modal-tier-icon advanced" />;
      case 'pro':
        return <Zap size={32} className="modal-tier-icon pro" />;
      default:
        return <Lock size={32} className="modal-tier-icon" />;
    }
  };

  return (
    <div className="upgrade-modal-overlay" onClick={closeUpgradeModal}>
      <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={closeUpgradeModal}>
          <X size={20} />
        </button>

        <div className="modal-header">
          <div className="modal-icon-wrapper">
            <Lock size={48} className="lock-icon" />
          </div>
          <h2>Upgrade Required</h2>
        </div>

        <div className="modal-body">
          <p className="feature-locked-text">
            <strong>{featureName}</strong> is not available on your current plan.
          </p>
          
          <div className="required-tier-card">
            {getTierIcon()}
            <div className="required-tier-info">
              <span className="required-label">Unlock with</span>
              <span className="required-tier-name">{requiredConfig?.name || 'Premium'}</span>
              <span className="required-tier-price">{requiredConfig?.displayPrice || ''}</span>
            </div>
          </div>

          <p className="upgrade-benefit">
            Upgrade now to unlock {featureName.toLowerCase()} and accelerate your research!
          </p>
          
          {trialAvailable && (
            <div className="trial-offer">
              <Eye size={18} />
              <span>Want to try it first? Preview this feature once for free!</span>
            </div>
          )}
          
          {trialUsed && (
            <div className="trial-used-notice">
              <span>You've already used your free trial for this feature.</span>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {trialAvailable ? (
            <>
              <button className="modal-btn trial" onClick={handleStartTrial}>
                <Eye size={16} /> Try Once Free
              </button>
              <button className="modal-btn primary" onClick={handleUpgrade}>
                View Plans <ArrowRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button className="modal-btn secondary" onClick={closeUpgradeModal}>
                Maybe Later
              </button>
              <button className="modal-btn primary" onClick={handleUpgrade}>
                View Plans <ArrowRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
